import { useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import type { IMessage } from 'src/types/Message';

type FeedLocationState = {
    leadDetails?: { id: number; scope: string };
    [key: string]: unknown;
};

// Keep the feed mounted: Back closes details without clearing filters or loaded pages.
export default function useLeadDetails(messages: IMessage[], scope: string) {
    const history = useHistory<FeedLocationState>();
    const location = useLocation<FeedLocationState>();
    const selection = location.state?.leadDetails;
    const selected = selection?.scope === scope ? messages.find((message) => message.id === selection.id) : undefined;
    const trigger = useRef<HTMLElement | null>(null);
    const wasOpen = useRef(false);

    useEffect(() => {
        if (!('scrollRestoration' in window.history)) return;
        const previous = window.history.scrollRestoration;
        // Same-page Back must retain the current feed position, including after switching leads.
        // Otherwise the browser restores the position of the first opened card.
        window.history.scrollRestoration = 'manual';
        return () => {
            window.history.scrollRestoration = previous;
        };
    }, []);

    const open = useCallback(
        (id: number, element: HTMLElement) => {
            trigger.current = element;
            const next = { ...location, state: { ...location.state, leadDetails: { id, scope } } };
            if (selection) history.replace(next);
            else history.push(next);
        },
        [history, location, scope, selection]
    );

    const close = useCallback(() => history.goBack(), [history]);

    useEffect(() => {
        if (selection && selection.scope !== scope) {
            const { leadDetails: _selection, ...state } = location.state;
            history.replace({ ...location, state });
        }
    }, [history, location, scope, selection]);

    useEffect(() => {
        if (wasOpen.current && !selected && trigger.current?.isConnected) {
            trigger.current.focus({ preventScroll: true });
        }
        wasOpen.current = !!selected;
    }, [selected]);

    return { selected, open, close };
}
